import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Send credentials on all API requests EXCEPT known public-only endpoints
// This ensures auth cookies are always sent for protected routes
axios.interceptors.request.use((config) => {
  const url = config.url || "";
  const isPublicOnly = (url.includes("/listings?") ||
    url.includes("/listings/autocomplete") || url.includes("/dealers?") || url.includes("/dealers/verified") ||
    url.includes("/sitemap") || url.includes("/category-content") || url.includes("/posts?") ||
    url.includes("/posts/by-slug") || url.includes("/ads/serve") ||
    url.includes("/ads/impression") || url.includes("/ads/click") ||
    url.includes("/clubs?") || (url.includes("/clubs/") && !url.includes("/admin/")) ||
    url.includes("/price-check") ||
    (url.includes("/listing/") && !url.includes("/create"))) && !url.includes("/admin/");
  if (!isPublicOnly || config.withCredentials) {
    config.withCredentials = true;
  }
  return config;
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    setUser(res.data.user);
    return res.data;
  };

  const register = async (data) => {
    const res = await axios.post(`${API}/auth/register`, data);
    setUser(res.data.user);
    return res.data;
  };

  const loginWithGoogle = (intendedAccountType = null) => {
    // Persist user's intent (buyer/private_seller/dealer) across the Google
    // OAuth redirect. AuthCallback reads this back and sends to the backend
    // so new users get the correct account_type instead of defaulting to buyer.
    if (intendedAccountType) {
      sessionStorage.setItem("gb_intended_account_type", intendedAccountType);
    } else {
      sessionStorage.removeItem("gb_intended_account_type");
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // redirectUrl MUST be derived from window.location.origin (dynamic) so the
    // user returns to the same domain they're on (preview / prod / custom).
    // The auth.emergentagent.com URL itself is the official Emergent-managed
    // Google OAuth endpoint and is the same across all environments by design.
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {}
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, loginWithGoogle, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
